import React, { BaseSyntheticEvent, useEffect, useState } from "react";
import { TextField, CircularProgress, Modal } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import "./AgencyLegalInfo.scss";
import AgencyProfileSteps from "./AgencyProfileSteps";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CommonEnums } from "../../../enums/common.enums";
import { AgencyApi } from "../../../utils/api/Agency";
import { LocalStorageHelpers } from "../../../utils/helpers/localstorage.helpers";
import { clientRoutes } from "../../../utils/routes";
import ProtectedComponent from "../../../components/ProtectedComponent";
import CustomPageLoader from "../../../components/loaders/CustomPageLoader";
import RoundTrueWhiteIcon from "../../../components/icons/RoundTrueWhiteIcon";
import BrandInfo from "./brandInfo/BrandInfo";
import AgentCreatingAgencySteps from "./AgencyProfileSteps";
import ErrorSummary from "./ErrorSummary";
import { BrandApi } from "../../../utils/api/Brand";

const AgentAgencyLegalInfo = () => {
  const navigate = useNavigate();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const openLogoutModal = () => setIsLogoutModalOpen(true);
  const closeLogoutModal = () => setIsLogoutModalOpen(false);
  const [state, setState] = useState({
    isLoading: false,
    loadingData: true,
    agencyData: null as any,
    validationCode: "61",
    isAustralia: true,
    isValidatingABN: false,
    isValidatingTax: false,
  });

  const initialValues = {
    agencyTradingName: "",
    agencyLegalName: "",
    abnNumber: "",
    acnNumber: "",
    agency_type: "",
    brand_id: "",
    brand_name: "",
    abn_stripe_validation: "false",
    acn_stripe_validation: "false",
    tax_number_stripe_validation: "false",
  };

  const onSubmitHandler = async (values: any) => {
    // ============================================
    // TODO: Enable Stripe validation later
    // ============================================
    // if (state.isAustralia) {
    //   if (values.abn_stripe_validation !== "true") {
    //     toast.error("Please validate your ABN Number before submitting");
    //     return;
    //   }
    // } else {
    //   const isBusinessValidated = values.abn_stripe_validation === "true";
    //   const isTaxValidated = values.tax_number_stripe_validation === "true";

    //   if (!isBusinessValidated && !isTaxValidated) {
    //     toast.error(
    //       "Please validate either your Business Number or Tax Number to proceed",
    //     );
    //     return;
    //   }
    // }

    setState((prev) => ({ ...prev, isLoading: true }));

    let formData = new FormData();
    const agencyData = state?.agencyData;

    formData.append("agency_trading_name", values?.agencyTradingName);
    formData.append("agency_legal_name", values?.agencyLegalName);
    formData.append("abn_number", values?.abnNumber);
    formData.append("acn_number", values?.acnNumber);
    formData.append("agency_type", values?.agency_type);

    formData.append(
      "registration_status",
      CommonEnums.agentRegistrationStatus.pending_for_brand_grammar,
    );

    formData.append("automatic_timezone", "true");

    formData.append("abn_stripe_validation", values.abn_stripe_validation);

    formData.append(
      "acn_stripe_validation",
      state.isAustralia ? values.acn_stripe_validation : "false",
    );
    formData.append(
      "tax_number_stripe_validation",
      !state.isAustralia ? values.tax_number_stripe_validation : "false",
    );

    if (values.agency_type === "brand" && values.brand_id) {
      formData.append("brand_id", values?.brand_id);
    }

    let agencyId = agencyData?._id ?? agencyData?.id;

    if (!agencyId) {
      toast.error("Agency ID not found. Please login again.");
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      let res = await AgencyApi.updateAgencyProfile(formData, agencyId);

      if (res?.data?.success) {
        const agencyId = agencyData?._id ?? agencyData?.id;

        const freshAgency = await AgencyApi.getAgencyById({
          agency_id: agencyId,
        });

        if (freshAgency.success) {
          const newAgencyData = freshAgency.data?.data;

          LocalStorageHelpers.setAgencyData({
            data: newAgencyData,
          });
        }

        navigate(clientRoutes.agentCreateAgencyBrandGrammar);
      } else {
        toast.error(res?.data?.message || "Failed to save information");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }

    setState((prev) => ({ ...prev, isLoading: false }));
  };

  const validationSchema = Yup.object({
    agencyTradingName: Yup.string().required("is required"),
    agencyLegalName: Yup.string().required("is required"),

    abnNumber: Yup.string()
      .required(state.isAustralia ? "is required" : "is required")
      .test("len", "ABN must be exactly 11 digits", (val) => {
        if (state.isAustralia && val) return val.length === 11;
        return true;
      }),

    acnNumber: Yup.string().when([], {
      is: () => state.isAustralia,
      then: (schema) =>
        schema
          .nullable()
          .notRequired()
          .test("len", "ACN must be exactly 9 digits", (val) => {
            if (!val) return true;
            return val.length === 9;
          }),
      otherwise: (schema) => schema.required("required"),
    }),

    agency_type: Yup.string().required("Please select an agency type"),
    brand_id: Yup.string().when("agency_type", {
      is: (val: string) => val === "brand",
      then: (schema) => schema.required("Please select your brand"),
      otherwise: (schema) => schema.optional(),
    }),

        // ============================================
    // TODO: Enable Stripe validation later
    // ============================================
    // abn_stripe_validation: Yup.string().test(
    //   "abn-validated",
    //   "Please validate your ABN Number",
    //   function (val) {
    //     if (state.isAustralia) {
    //       return val === "true";
    //     }
    //     return true;
    //   },
    // ),

    // tax_number_stripe_validation: Yup.string().test(
    //   "business-or-tax-validated",
    //   "Please validate either Business Number or Tax Number",
    //   function (val) {
    //     if (!state.isAustralia) {
    //       const businessValidated =
    //         this.parent.abn_stripe_validation === "true";
    //       const taxValidated = val === "true";
    //       return businessValidated || taxValidated;
    //     }
    //     return true;
    //   },
    // ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: onSubmitHandler,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      const storedAgency: any = LocalStorageHelpers.getAgencyData();

      if (!storedAgency?._id) {
        // navigate(clientRoutes.login);
        return;
      }

      try {
        const res = await AgencyApi.getAgencyById({
          agency_id: storedAgency._id,
        });

        if (!res.success) {
          toast.error("Failed to fetch agency data");
          // navigate(clientRoutes.login);
          return;
        }

        const agencyData = res.data?.data;

        LocalStorageHelpers.setAgencyData({
          data: agencyData,
        });

        const isAU =
          agencyData?.country_code === "AU" ||
          (agencyData?.phone_country_code || "61").replace("+", "") === "61";

        setState((prev) => ({
          ...prev,
          agencyData,
          loadingData: false,
          isAustralia: isAU,
          validationCode: agencyData?.phone_country_code || "61",
        }));

        formik.setFieldValue(
          "agencyTradingName",
          agencyData?.agency_trading_name || "",
        );
        formik.setFieldValue(
          "agencyLegalName",
          agencyData?.agency_legal_name || "",
        );
        formik.setFieldValue("abnNumber", agencyData?.abn_number || "");
        formik.setFieldValue("acnNumber", agencyData?.acn_number || "");

        if (agencyData?.abn_stripe_validation)
          formik.setFieldValue("abn_stripe_validation", "true");

        if (agencyData?.acn_stripe_validation)
          formik.setFieldValue("acn_stripe_validation", "true");

        if (agencyData?.tax_number_stripe_validation)
          formik.setFieldValue("tax_number_stripe_validation", "true");

        if (agencyData?.agency_type)
          formik.setFieldValue("agency_type", agencyData?.agency_type);

        const bId =
          typeof agencyData?.brand_id === "object"
            ? agencyData?.brand_id?._id
            : agencyData?.brand_id || "";

        const bName =
          typeof agencyData?.brand_id === "object"
            ? agencyData?.brand_id?.brand_name
            : agencyData?.brand_name || "";

        formik.setFieldValue("brand_id", bId);
        formik.setFieldValue("brand_name", bName);

        if (bId && !bName) {
          try {
            const brandRes = await BrandApi.getBrandById(bId);
            if (brandRes?.data?.success) {
              formik.setFieldValue(
                "brand_name",
                brandRes.data.data?.brand_name || "",
              );
            }
          } catch (e) {
            console.error("Failed to fetch brand name", e);
          }
        }
      } catch (err) {
        toast.error("Something went wrong");
        // navigate(clientRoutes.login);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (e: BaseSyntheticEvent) => {
    const name = e.target.name;
    let value = e.target.value;
    const isAU = state.isAustralia;

    if (name === "abnNumber")
      formik.setFieldValue("abn_stripe_validation", "false");
    if (name === "acnNumber") {
      formik.setFieldValue("acn_stripe_validation", "false");
      formik.setFieldValue("tax_number_stripe_validation", "false");
    }

    if (isAU) {
      value = value.replace(/\D/g, "");
    }

    if (isAU && name === "abnNumber" && value.length > 11) return;
    if (isAU && name === "acnNumber" && value.length > 9) return;

    formik.setFieldValue(name, value);
  };

  const handleAgencyTypeSelect = (type: string) => {
    formik.setFieldValue("agency_type", type);
  };

  const handleValidateABN = () => {
    if (!formik.values.abnNumber) {
      toast.error("Please enter an ABN number first");
      return;
    }
    toast.success("ABN Validated successfully");
  };

  const handleLogout = () => {
    navigate(clientRoutes.login);
  };

  const handleLogoutConfirm = () => {
    closeLogoutModal();
    LocalStorageHelpers.clear();
    navigate(clientRoutes.login);
  };

  const handleBack = () => {
    navigate(clientRoutes.agentCreateAgencyVerifyPhone);
  };

  const handleValidateBusinessIdentifier = async () => {
    const isAU = state.isAustralia;
    const fieldLabel = isAU ? "ABN Number" : "Business Number";
    const val = formik.values.abnNumber;

    if (!val) {
      toast.error(`Please enter a ${fieldLabel} first`);
      return;
    }

    if (isAU && val.length !== 11) {
      toast.error("ABN Number must be 11 digits");
      return;
    }

    setState((prev) => ({ ...prev, isValidatingABN: true }));

    const res = await AgencyApi.validateTaxId({
      agency_id: state.agencyData?._id,
      value: val,
      type: isAU ? "au_abn" : "",
    });

    if (res.success) {
      toast.success(`${fieldLabel} Verified via Stripe!`);
      formik.setFieldValue("abn_stripe_validation", "true");
    } else {
      toast.error(res.error || `Invalid ${fieldLabel}`);
      formik.setFieldValue("abn_stripe_validation", "false");
    }

    setState((prev) => ({ ...prev, isValidatingABN: false }));
  };

  const handleValidateTaxNumber = async () => {
    const val = formik.values.acnNumber;

    if (!val) {
      toast.error("Please enter a Tax Number first");
      return;
    }

    setState((prev) => ({ ...prev, isValidatingTax: true }));

    const res = await AgencyApi.validateTaxId({
      agency_id: state.agencyData?._id,
      value: val,
      type: "",
    });

    if (res.success) {
      toast.success("Tax Number Verified!");
      formik.setFieldValue("tax_number_stripe_validation", "true");
    } else {
      toast.error(res.error || "Invalid Tax Number");
      formik.setFieldValue("tax_number_stripe_validation", "false");
    }

    setState((prev) => ({ ...prev, isValidatingTax: false }));
  };

  if (state.loadingData) {
    return (
      <ProtectedComponent>
        <div className="agency-legal-info-container">
          <div className="circular-progress-container">
            <CustomPageLoader />
          </div>
        </div>
      </ProtectedComponent>
    );
  }

  const isAbnVerified = formik.values.abn_stripe_validation === "true";
  const isTaxVerified =
    formik.values.tax_number_stripe_validation === "true" ||
    formik.values.acn_stripe_validation === "true";

  return (
    <ProtectedComponent>
      <div className="steps-container">
        <AgentCreatingAgencySteps
          step={CommonEnums.agencyProfileSteps.legalInfo}
          agencyData={state.agencyData}
        />
      </div>

      <div className="agency-legal-info-container">
        <div className="content-wrapper">
          <form className="form-container" onSubmit={formik.handleSubmit}>
            <div className="section-header">
              <h3>Legal & Tax Information</h3>
              <p className="subtitle">
                Provide the official registration details for your real estate
                agency
              </p>
            </div>

            <div className="double-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">Agency Trading Name*</p>
                <TextField
                  placeholder="Enter Agency Trading Name"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="agencyTradingName"
                  onChange={formik.handleChange}
                  value={formik.values.agencyTradingName}
                  error={Boolean(formik?.errors?.agencyTradingName)}
                />
              </div>

              <div className="input-and-lable-container">
                <p className="label font-inter-400">Agency Legal Name*</p>
                <TextField
                  placeholder="Enter Agency Legal Name"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="agencyLegalName"
                  onChange={formik.handleChange}
                  value={formik.values.agencyLegalName}
                  error={Boolean(formik?.errors?.agencyLegalName)}
                />
              </div>
            </div>

            <div className="double-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">
                  {state.isAustralia ? "ABN Number*" : "Business Number*"}
                </p>
                <div className="abn-input-group">
                  <TextField
                    placeholder={
                      state.isAustralia
                        ? "Enter ABN Number"
                        : "i.e. EIN/BN/NZBN/CRN Number*"
                    }
                    variant="outlined"
                    fullWidth
                    className={`gray-bg-input ${isAbnVerified ? "verified-input-border" : ""}`}
                    name="abnNumber"
                    onChange={handleInputChange}
                    value={formik.values.abnNumber}
                    error={Boolean(formik?.errors?.abnNumber)}
                  />
                  <button
                    type="button"
                    className={`validate-btn ${isAbnVerified ? "verified" : ""}`}
                    onClick={handleValidateBusinessIdentifier}
                    disabled={state.isValidatingABN || isAbnVerified}
                  >
                    {state.isValidatingABN ? (
                      <CircularProgress size={20} style={{ color: "white" }} />
                    ) : isAbnVerified ? (
                      <div className="verified-content">
                        <RoundTrueWhiteIcon />
                        <span>Validated</span>
                      </div>
                    ) : (
                      "Validate"
                    )}
                  </button>
                </div>
              </div>

              <div className="input-and-lable-container">
                <p className="label font-inter-400">
                  {state.isAustralia ? "ACN Number" : "Tax Number*"}
                </p>

                {state.isAustralia ? (
                  <TextField
                    placeholder="Enter ACN Number"
                    variant="outlined"
                    fullWidth
                    className="gray-bg-input"
                    name="acnNumber"
                    onChange={handleInputChange}
                    value={formik.values.acnNumber}
                    error={Boolean(formik?.errors?.acnNumber)}
                  />
                ) : (
                  <div className="abn-input-group">
                    <TextField
                      placeholder="i.e. GST/VAT/Sale Tax Number*"
                      variant="outlined"
                      fullWidth
                      className={`gray-bg-input ${isTaxVerified ? "verified-input" : ""}`}
                      name="acnNumber"
                      onChange={handleInputChange}
                      value={formik.values.acnNumber}
                      error={Boolean(formik?.errors?.acnNumber)}
                    />
                    <button
                      type="button"
                      className={`validate-btn ${isTaxVerified ? "verified" : ""}`}
                      onClick={handleValidateTaxNumber}
                      disabled={state.isValidatingTax || isTaxVerified}
                    >
                      {state.isValidatingTax ? (
                        <CircularProgress
                          size={20}
                          style={{ color: "white" }}
                        />
                      ) : isTaxVerified ? (
                        "Verified"
                      ) : (
                        "Validate"
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

                        {!state.isAustralia && (
              <p className="legal-info-note">
                Please validate either your Business Number or Tax Number to
                proceed. Only one is required.
              </p>
            )}

            <BrandInfo
  formik={formik}
  countryCode={state?.agencyData?.country_code || ""}
/>

            <ErrorSummary errors={formik.errors as Record<string, string>} />

            <div className="action-buttons">
              <button type="button" className="back-btn" onClick={handleBack}>
                Back
              </button>
              <button
                className="continue-btn"
                disabled={state?.isLoading}
                type="button"
                onClick={async () => {
                  const errors = await formik.validateForm();

                  if (Object.keys(errors).length > 0) {
                    formik.setTouched(
                      Object.keys(errors).reduce((acc, key) => {
                        acc[key] = true;
                        return acc;
                      }, {} as any),
                    );
                    return;
                  }

                  formik.handleSubmit();
                }}
              >
                {state?.isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Save & continue"
                )}
              </button>
            </div>

            <div className="form-footer">
              <p>
                By continuing, you agree to our <a>Terms and Conditions</a>
              </p>
              <p>
                Read our <a>Personal Information Collection Statement</a>
              </p>

              <p style={{ marginTop: "20px" }}>
                Profile progress saved.
                <br />
                Feel free to log out; you can continue your setup at any time.
              </p>
              <span className="logout-link" onClick={openLogoutModal}>
                Logout
              </span>
            </div>
          </form>
        </div>
      </div>

      <Modal
        open={isLogoutModalOpen}
        onClose={(_event, reason) => {
          if (reason !== "backdropClick") closeLogoutModal();
          else closeLogoutModal();
        }}
        aria-labelledby="logout-modal-title"
        aria-describedby="logout-modal-desc"
        className="logout-confirm-modal"
      >
        <div className="logout-modal-center">
          <div className="logout-modal-card">
            <h3 id="logout-modal-title" className="logout-modal-title">
              Log out?
            </h3>

            <p id="logout-modal-desc" className="logout-modal-subtitle">
              Unsaved changes will be discarded.
              <br />
              Are you sure you want to proceed?
            </p>

            <div className="logout-modal-actions">
              <button
                type="button"
                className="logout-cancel-btn"
                onClick={closeLogoutModal}
              >
                Cancel
              </button>

              <button
                type="button"
                className="logout-confirm-btn"
                onClick={handleLogoutConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </ProtectedComponent>
  );
};

export default AgentAgencyLegalInfo;
